import { MagnifyingGlassIcon } from '@phosphor-icons/react/MagnifyingGlass'
import { useEffect, useMemo, useRef, useState } from 'react'

export type WorkspaceCommand = {
  id: string
  label: string
  description: string
  shortcut?: string
  disabled?: boolean
  onExecute: () => void
}

type CommandPaletteProps = {
  open: boolean
  commands: readonly WorkspaceCommand[]
  onClose: () => void
}

/** Small, dependency-free command surface. The command list is provided by Workbench so domain state stays in one place. */
export function CommandPalette({ open, commands, onClose }: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const visibleCommands = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    if (!needle) return commands.filter((command) => !command.disabled)
    return commands.filter((command) => !command.disabled && `${command.label} ${command.description}`.toLocaleLowerCase().includes(needle))
  }, [commands, query])

  useEffect(() => {
    if (!open) return
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setQuery('')
    setActiveIndex(0)
    window.requestAnimationFrame(() => inputRef.current?.focus())
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('input, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? [])]
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }, [onClose, open])

  useEffect(() => { setActiveIndex(0) }, [query])

  if (!open) return null
  const activeCommand = visibleCommands[activeIndex]

  return (
    <div className="command-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={dialogRef}
        className="workspace-command-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workspace-command-title"
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex((index) => Math.min(Math.max(visibleCommands.length - 1, 0), index + 1))
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex((index) => Math.max(0, index - 1))
          }
          if (event.key === 'Enter' && activeCommand) {
            event.preventDefault()
            activeCommand.onExecute()
            onClose()
          }
        }}
      >
        <div className="workspace-command-heading"><strong id="workspace-command-title">Command palette</strong><span>⌘K</span></div>
        <label className="workspace-command-input">
          <MagnifyingGlassIcon aria-hidden="true" />
          <span className="sr-only">Search commands</span>
          <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search commands…" autoComplete="off" />
          <kbd>ESC</kbd>
        </label>
        <div className="workspace-command-list" role="listbox" aria-label="Workspace commands">
          {visibleCommands.length ? visibleCommands.map((command, index) => (
            <button
              key={command.id}
              type="button"
              role="option"
              aria-selected={activeIndex === index}
              className={`workspace-command-item ${activeIndex === index ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => { command.onExecute(); onClose() }}
            >
              <span><strong>{command.label}</strong><small>{command.description}</small></span>
              {command.shortcut && <kbd>{command.shortcut}</kbd>}
            </button>
          )) : <p className="workspace-command-empty">No commands match “{query}”.</p>}
        </div>
      </section>
    </div>
  )
}
